import React, { useState, useMemo } from 'react';
import { 
  ToolsIcon, CloseIcon, SearchIcon, PlusIcon, TrashIcon, EditIcon, LightbulbIcon,
  ChevronDownIcon,
  // Category icons
  CogIcon, FolderIcon, TerminalIcon, CodeIcon, GlobeIcon, BrainIcon, 
  LayersIcon, CloudIcon, WorkflowIcon, PackageIcon, AgentIcon
} from './Icons';

function ToolLibrary({ 
  builtinTools, 
  customTools, 
  onAddCustomTool, 
  onEditCustomTool, 
  onDeleteCustomTool,
  selectedAgent,
  onToggleToolForAgent,
  onClose
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('builtin');
  const [expandedCategories, setExpandedCategories] = useState({});

  const selectedAgentTools = selectedAgent?.data?.config?.tools || [];

  // Group built-in tools by category
  const groupedBuiltinTools = useMemo(() => {
    const groups = {};
    builtinTools.forEach(tool => {
      const category = tool.category || 'other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(tool);
    });
    return groups;
  }, [builtinTools]);

  // Filter tools based on search term
  const filteredBuiltinTools = useMemo(() => {
    if (!searchTerm) return builtinTools;
    return builtinTools.filter(tool =>
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tool.category && tool.category.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [builtinTools, searchTerm]);

  // Group filtered tools by category
  const filteredGroupedTools = useMemo(() => {
    const groups = {};
    filteredBuiltinTools.forEach(tool => {
      const category = tool.category || 'other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(tool);
    });
    return groups;
  }, [filteredBuiltinTools]);

  const filteredCustomTools = customTools.filter(tool =>
    tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tool.description && tool.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Category display names and icons (all icons use same color for consistency)
  const getCategoryIcon = (category) => {
    const iconProps = { size: 14, color: '#999' };
    switch (category) {
      case 'utilities': return <CogIcon {...iconProps} />;
      case 'file': return <FolderIcon {...iconProps} />;
      case 'system': return <TerminalIcon {...iconProps} />;
      case 'code': return <CodeIcon {...iconProps} />;
      case 'web': return <GlobeIcon {...iconProps} />;
      case 'memory': return <BrainIcon {...iconProps} />;
      case 'multimodal': return <LayersIcon {...iconProps} />;
      case 'aws': return <CloudIcon {...iconProps} />;
      case 'agents': return <WorkflowIcon {...iconProps} />;
      case 'knowledge_bases': return <BrainIcon {...iconProps} color="#60D2CC" />;
      case 'unlimited3d': return <LayersIcon {...iconProps} color="#FF6B35" />;
      case 'components': return <PackageIcon {...iconProps} color="#A855F7" />;
      case 'project': return <FolderIcon {...iconProps} color="#10B981" />;
      default: return <PackageIcon {...iconProps} />;
    }
  };

  const categoryInfo = {
    utilities: { name: 'Utilities' },
    file: { name: 'File Operations' },
    system: { name: 'Shell & System' },
    code: { name: 'Code' },
    web: { name: 'Web & Network' },
    memory: { name: 'RAG & Memory' },
    multimodal: { name: 'Multi-modal' },
    aws: { name: 'AWS Services' },
    agents: { name: 'Agents & Workflows' },
    knowledge_bases: { name: 'Your Knowledge Bases' },
    unlimited3d: { name: 'Threedium Tools' },
    components: { name: 'UI Components' },
    project: { name: 'Project Management' },
    other: { name: 'Other' }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Auto-expand categories when user starts searching, collapse when cleared
  React.useEffect(() => {
    if (searchTerm) {
      // Expand all categories that have matching tools
      // We compute this inline to avoid object dependency issues
      const matchingCategories = {};
      builtinTools
        .filter(tool =>
          tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (tool.category && tool.category.toLowerCase().includes(searchTerm.toLowerCase()))
        )
        .forEach(tool => {
          matchingCategories[tool.category || 'other'] = true;
        });
      setExpandedCategories(matchingCategories);
    } else {
      // Collapse all when search is cleared
      setExpandedCategories({});
    }
  }, [searchTerm, builtinTools]);

  return (
    <div className="flex flex-col" style={{ height: '100%' }}>
      {/* Header */}
      <div className="panel-header">
        <div className="panel-title">
          <div className="panel-title-icon">
            <ToolsIcon size={14} color="#60D2CC" />
          </div>
          <span>Tools</span>
        </div>
        <button className="panel-toggle" onClick={onClose} title="Close panel">
          <CloseIcon size={12} />
        </button>
      </div>

      {/* Search */}
      <div className="panel-section-divider" style={{ padding: 'var(--spacing-md)', paddingRight: 0 }}>
        <div className="search-wrapper">
          <span className="search-icon">
            <SearchIcon size={14} color="#999" />
          </span>
          <input
            type="text"
            className="input search-input"
            placeholder="Search tools..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Tabs */}
        <div className="tabs" style={{ marginTop: 'var(--spacing-md)' }}>
          <button
            className={`tab ${selectedTab === 'builtin' ? 'active' : ''}`}
            onClick={() => setSelectedTab('builtin')}
          >
            Built-in ({builtinTools.length})
          </button>
          <button
            className={`tab ${selectedTab === 'custom' ? 'active' : ''}`}
            onClick={() => setSelectedTab('custom')}
          >
            Custom ({customTools.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="panel-content" style={{ padding: 'var(--spacing-md)', paddingRight: 0 }}>
        {/* Agent Selection Notice */}
        {!selectedAgent && (
          <div className="alert alert-info" style={{ marginBottom: 'var(--spacing-md)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <LightbulbIcon size={14} color="#60D2CC" style={{ marginTop: '2px', flexShrink: 0 }} />
            <span><strong>Tip:</strong> Select an agent node to assign tools to it.</span>
          </div>
        )}

        {selectedAgent && (
          <div className="alert alert-success" style={{ marginBottom: 'var(--spacing-md)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AgentIcon size={14} color="#10B981" style={{ flexShrink: 0 }} />
              <strong>{selectedAgent.data.config?.name || 'Agent'}</strong>
            </div>
            <div style={{ fontSize: '12px', marginTop: '6px', opacity: 0.8 }}>
              Click tools below to toggle assignment
            </div>
          </div>
        )}

        {/* Built-in Tools Tab */}
        {selectedTab === 'builtin' && (
          <>
            {filteredBuiltinTools.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <SearchIcon size={24} color="#999" />
                </div>
                <div className="empty-state-text">No tools found</div>
              </div>
            ) : (
              Object.keys(filteredGroupedTools)
                .sort()
                .map(category => {
                  const tools = filteredGroupedTools[category];
                  const isExpanded = expandedCategories[category] === true;
                  const catInfo = categoryInfo[category] || categoryInfo.other;
                  
                  return (
                    <CategoryGroup
                      key={category}
                      category={category}
                      categoryInfo={catInfo}
                      categoryIcon={getCategoryIcon(category)}
                      tools={tools}
                      isExpanded={isExpanded}
                      onToggle={() => toggleCategory(category)}
                      selectedAgentTools={selectedAgentTools}
                      selectedAgent={selectedAgent}
                      onToggleToolForAgent={onToggleToolForAgent}
                    />
                  );
                })
            )}
          </>
        )}

        {/* Custom Tools Tab */}
        {selectedTab === 'custom' && (
          <>
            <button
              className="btn btn-success btn-block"
              onClick={onAddCustomTool}
              style={{ marginBottom: 'var(--spacing-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <PlusIcon size={14} />
              <span>Create Custom Tool</span>
            </button>

            {filteredCustomTools.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <ToolsIcon size={24} color="#999" />
                </div>
                <div className="empty-state-text">
                  {customTools.length === 0 
                    ? 'No custom tools yet. Create one!' 
                    : 'No matching custom tools'}
                </div>
              </div>
            ) : (
              filteredCustomTools.map(tool => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  isAssigned={selectedAgentTools.includes(tool.id)}
                  onToggle={() => selectedAgent && onToggleToolForAgent(selectedAgent.id, tool.id)}
                  canToggle={!!selectedAgent}
                  isCustom={true}
                  onEdit={() => onEditCustomTool(tool)}
                  onDelete={() => onDeleteCustomTool(tool.id)}
                />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ToolCard({ tool, isAssigned, onToggle, canToggle, isCustom, onEdit, onDelete }) {
  return (
    <div 
      className={`tool-card ${isAssigned ? 'assigned' : ''}`}
      onClick={() => canToggle && onToggle()}
      style={{ cursor: canToggle ? 'pointer' : 'default' }}
    >
      <div className="tool-card-header">
        {canToggle && (
          <input
            type="checkbox"
            className="tool-checkbox"
            checked={isAssigned}
            readOnly
          />
        )}
        
        <div style={{ flex: 1 }}>
          <div className="tool-name">
            {tool.name}
            {isAssigned && (
              <span className="badge badge-success" style={{ marginLeft: '8px' }}>
                Assigned
              </span>
            )}
          </div>
          
          <div className="tool-description">
            {tool.description}
          </div>

          {isCustom && tool.parameters && tool.parameters.length > 0 && (
            <div style={{ 
              marginTop: '8px',
              fontFamily: 'var(--font-mono)',
              fontSize: '10px',
              color: 'var(--text-muted)'
            }}>
              Params: {tool.parameters.map(p => p.name).join(', ')}
            </div>
          )}
        </div>

        {isCustom && (
          <div 
            style={{ display: 'flex', gap: '4px' }} 
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="btn btn-sm btn-ghost"
              onClick={onEdit}
              title="Edit tool"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <EditIcon size={14} />
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => {
                if (window.confirm(`Delete tool "${tool.name}"?`)) {
                  onDelete();
                }
              }}
              title="Delete tool"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <TrashIcon size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryGroup({ 
  category, 
  categoryInfo, 
  categoryIcon,
  tools, 
  isExpanded, 
  onToggle, 
  selectedAgentTools, 
  selectedAgent, 
  onToggleToolForAgent 
}) {
  const assignedCount = tools.filter(t => selectedAgentTools.includes(t.id)).length;

  return (
    <div className="category-group">
      <div className="category-header" onClick={onToggle}>
        <div className="category-info">
          <span className="category-icon">{categoryIcon}</span>
          <span className="category-name">{categoryInfo.name}</span>
          <span 
            className="category-count"
            style={{ 
              background: assignedCount > 0 ? 'rgba(16, 185, 129, 0.2)' : 'var(--bg-hover)',
              color: assignedCount > 0 ? 'var(--accent-success)' : 'var(--text-tertiary)'
            }}
          >
            {assignedCount > 0 ? `${assignedCount}/` : ''}{tools.length}
          </span>
        </div>
        <span className={`category-toggle ${isExpanded ? 'expanded' : ''}`}>
          <ChevronDownIcon size={16} color="#fff" />
        </span>
      </div>
      
      {isExpanded && (
        <div className="category-content">
          {tools.map(tool => (
            <ToolCard
              key={tool.id}
              tool={tool}
              isAssigned={selectedAgentTools.includes(tool.id)}
              onToggle={() => selectedAgent && onToggleToolForAgent(selectedAgent.id, tool.id)}
              canToggle={!!selectedAgent}
              isCustom={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ToolLibrary;
